import { FormEvent, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Cpu, Eye, EyeOff, Globe2, Lock, Shield, User } from 'lucide-react';
import type { AuthUser } from '@ukrop-nm/shared';
import { useAuth } from '../auth/AuthContext';
import { ACCOUNT_CHANGED_EVENT } from '../auth/accountScope';
import { loadUserUiSettings, saveUserUiSettings } from '../auth/userSettings';
import { BrandMark } from '../brand/BrandMark';
import { countAllDiagramDevices } from '../state/deviceCount';

/** 24 години офісного трафіку: ніч тихо, ранок/день пік, обід провал, вечір спад. */
const NETWORK_SPARK = [
  18, 14, 12, 11, 13, 18, 32, 55, 78, 86, 82, 88, 64, 72, 90, 92, 85, 70, 48, 36, 28, 24, 20, 18,
];

function sparkGeometry(values: number[], width: number, height: number, pad = 3) {
  const step = width / (values.length - 1);
  const points = values.map((value, i) => {
    const x = i * step;
    const y = pad + (1 - value / 100) * (height - pad * 2);
    return { x, y };
  });
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L ${points[points.length - 1].x.toFixed(1)} ${height} L 0 ${height} Z`;
  return { line, area, peak: points.reduce((best, p) => (p.y < best.y ? p : best)) };
}

function NetworkSparkline() {
  const { line, area, peak } = sparkGeometry(NETWORK_SPARK, 200, 56);
  return (
    <svg viewBox="0 0 200 56" className="mt-2 h-12 w-full" aria-hidden>
      <defs>
        <linearGradient id="login-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.42" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" y1="14" x2="200" y2="14" stroke="rgba(255,255,255,0.08)" />
      <line x1="0" y1="28" x2="200" y2="28" stroke="rgba(255,255,255,0.08)" />
      <line x1="0" y1="42" x2="200" y2="42" stroke="rgba(255,255,255,0.08)" />
      <path d={area} fill="url(#login-spark-fill)" />
      <path d={line} fill="none" stroke="#60A5FA" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={peak.x} cy={peak.y} r="2.4" fill="#93C5FD" />
    </svg>
  );
}

const ERRORS: Record<string, string> = {
  oauth: 'Соцмережі та зовнішні IdP вимкнено.',
  provider: 'Цей спосіб входу вимкнено.',
  session: 'На сервері немає секрету сесії.',
  state: 'Сеанс входу застарів. Почніть знову.',
  credentials: 'Невірний логін або пароль.',
  password: 'Вхід за паролем тимчасово недоступний (немає Neon DATABASE_URL).',
  closed: 'Реєстрація вимкнена.',
};

const FEATURES = [
  {
    icon: Globe2,
    title: 'Моніторинг у реальному часі',
    text: 'Контроль та аналіз вашої мережі.',
  },
  {
    icon: Shield,
    title: 'Безпека та стабільність',
    text: 'Надійний захист даних.',
  },
  {
    icon: Cpu,
    title: 'Керування пристроями',
    text: 'Просте управління всіма пристроями.',
  },
  {
    icon: BarChart3,
    title: 'Аналітика та звіти',
    text: 'Детальна статистика та звіти.',
  },
];

function UkropMark() {
  return <BrandMark variant="lockup" />;
}

export function LoginPage({ mode }: { mode: 'login' | 'register' }) {
  const { passwordLogin, user, ready, setSessionUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => loadUserUiSettings().rememberLogin);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deviceCount, setDeviceCount] = useState(() => countAllDiagramDevices());
  const errorBoxRef = useRef<HTMLDivElement>(null);
  const next = params.get('next') || '/';
  const error = params.get('error');
  const shownError = formError || (error ? ERRORS[error] || 'Помилка входу.' : null);

  useEffect(() => {
    const refresh = () => setDeviceCount(countAllDiagramDevices());
    refresh();
    window.addEventListener(ACCOUNT_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(ACCOUNT_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    if (shownError) errorBoxRef.current?.focus();
  }, [shownError]);

  const title = 'Вхід у систему';
  const subtitle = 'Будь ласка, увійдіть у свій обліковий запис';

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!passwordLogin) {
      setFormError(ERRORS.password);
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/password/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          remember,
          next,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        user?: AuthUser;
        next?: string;
      };
      if (!res.ok) {
        setFormError(body.message || ERRORS[body.error || ''] || 'Помилка входу.');
        return;
      }
      if (body.user) await setSessionUser(body.user);
      navigate(safeClientNext(body.next || next));
    } catch {
      setFormError('Немає звʼязку з сервером входу.');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'register') {
    return <Navigate to="/login" replace />;
  }

  if (ready && user) {
    return <Navigate to={safeClientNext(next)} replace />;
  }

  return (
    <div className="login-page">
      <a href="#login-form" className="skip-link">
        Перейти до форми входу
      </a>
      <div className="login-split">
        <section className="login-left">
          <header className="login-brand">
            <UkropMark />
          </header>

          <div>
            <h1 className="text-[28px] leading-none font-semibold tracking-tight">{title}</h1>
            <p className="mt-2 text-sm text-white/80">{subtitle}</p>
          </div>

          <div className="login-card">
            {shownError ? (
              <div
                ref={errorBoxRef}
                id="login-error-summary"
                role="alert"
                tabIndex={-1}
                className="mb-4 rounded-lg border border-orange-400/40 bg-orange-500/15 px-3 py-2 text-sm text-orange-50 outline-none"
              >
                <p className="font-semibold">Не вдалося увійти</p>
                <p className="mt-1">{shownError}</p>
                <p className="mt-2">
                  <a className="underline" href="#login-email">
                    Перейти до логіну
                  </a>
                  {' · '}
                  <a className="underline" href="#login-password">
                    Перейти до пароля
                  </a>
                </p>
              </div>
            ) : null}

            {!passwordLogin && ready ? (
              <p className="mb-4 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white/80">
                Сервер входу ще не налаштований (немає парольного акаунта).
              </p>
            ) : null}

            <form id="login-form" className="space-y-3" onSubmit={onSubmit}>
              <label className="block text-sm text-white/80" htmlFor="login-email">
                Логін (email)
                <span className="login-field">
                  <User size={16} className="shrink-0 text-white/50" aria-hidden />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    aria-invalid={shownError ? true : undefined}
                    aria-describedby={shownError ? 'login-email-error login-error-summary' : undefined}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Введіть email"
                  />
                </span>
              </label>
              {shownError ? (
                <p id="login-email-error" className="text-sm text-orange-100">
                  Перевірте адресу електронної пошти.
                </p>
              ) : null}

              <label className="block text-sm text-white/80" htmlFor="login-password">
                Пароль
                <span className="login-field">
                  <Lock size={16} className="shrink-0 text-white/50" aria-hidden />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    aria-invalid={shownError ? true : undefined}
                    aria-describedby={shownError ? 'login-password-error login-error-summary' : undefined}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Введіть пароль"
                  />
                  <button
                    type="button"
                    className="text-white/80"
                    aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
                  </button>
                </span>
              </label>
              {shownError ? (
                <p id="login-password-error" className="text-sm text-orange-100">
                  Перевірте пароль.
                </p>
              ) : null}

              <div className="flex items-center justify-between gap-3 text-sm">
                <label className="inline-flex min-h-11 items-center gap-2 text-white/80">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => {
                      setRemember(e.target.checked);
                      saveUserUiSettings({ rememberLogin: e.target.checked });
                    }}
                    className="h-4 w-4 rounded border-white/20 bg-transparent accent-brand-500"
                  />
                  Запамʼятати мене
                </label>
                <button
                  type="button"
                  className="min-h-11 text-brand-300 underline-offset-2 hover:underline"
                  onClick={() => setForgotOpen((v) => !v)}
                >
                  Забули пароль?
                </button>
              </div>

              {forgotOpen ? (
                <p className="text-sm text-white/80">
                  Самостійне відновлення вимкнено. Зверніться до адміністратора UKROP.
                </p>
              ) : null}

              <button
                type="submit"
                disabled={busy || !passwordLogin}
                className="flex h-11 w-full items-center justify-center rounded-lg bg-brand-500 text-sm font-semibold text-white transition hover:bg-brand-600 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#12151e] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Вхід…' : 'Увійти'}
              </button>
            </form>

            <p className="mt-3 text-sm text-white/80">
              Немає облікового запису? Реєстрацію вимкнено — лише виданий логін і пароль.
            </p>
          </div>
        </section>

        <aside className="login-right">
          <div className="login-stats">
            <p className="text-xs tracking-[0.16em] text-white/75 uppercase">Мережа підключень</p>
            <NetworkSparkline />
            <p className="mt-3 text-xs tracking-[0.16em] text-white/75 uppercase">Активні пристрої</p>
            <p className="mt-1 text-[28px] leading-none font-semibold text-[#3B82F6]">{deviceCount}</p>
          </div>

          <div className="login-feat">
            {FEATURES.map((item) => (
              <div key={item.title} className="flex items-start gap-2.5">
                <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#3B82F6]" strokeWidth={1.5} />
                <div>
                  <div className="text-sm font-medium text-white">{item.title}</div>
                  <div className="mt-0.5 text-sm leading-5 text-white/75">{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
      <p className="login-legal">© 2026 UKROP. Всі права захищені.</p>
    </div>
  );
}

function safeClientNext(raw: string): string {
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return '/';
  return raw;
}
